import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Policies from "@/pages/Policies";
import PolicyDetail from "@/pages/PolicyDetail";
import Alerts from "@/pages/Alerts";
import RiskAssessment from "@/pages/RiskAssessment";
import CommentGenerator from "@/pages/CommentGenerator";
import Settings from "@/pages/Settings";
import Layout from "@/components/Layout";
// Community Hub Pages
import CommunityHub from "@/pages/community/CommunityHub";
import CommunityOnboarding from "@/pages/community/CommunityOnboarding";
import CommunityProfile from "@/pages/community/CommunityProfile";
import DiscoverAdvocates from "@/pages/community/DiscoverAdvocates";
import MyConnections from "@/pages/community/MyConnections";
import Groups from "@/pages/community/Groups";
import Messages from "@/pages/community/Messages";
import Privacy from "@/pages/settings/Privacy";
// Corporate Portal
import CorporateDashboard from "@/pages/corporate/CorporateDashboard";
// Admin Portal
import AdminDashboard from "@/pages/admin/AdminDashboard";
// Deed Theft
import DeedTheft from "@/pages/DeedTheft";
// Community Meetings
import CommunityMeetings from "@/pages/CommunityMeetings";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Translations
const translations = {
  en: {
    nav: {
      dashboard: "Dashboard",
      policies: "Policies",
      alerts: "Alerts",
      risk: "Risk Assessment",
      comments: "Comment Generator",
      settings: "Settings",
      logout: "Logout",
      login: "Login",
      register: "Register"
    },
    common: {
      loading: "Loading...",
      save: "Save",
      cancel: "Cancel",
      submit: "Submit",
      search: "Search",
      filter: "Filter",
      all: "All",
      high: "High",
      medium: "Medium",
      low: "Low",
      federal: "Federal",
      state: "State",
      local: "Local"
    },
    landing: {
      title: "Early Warnings for Safer Communities",
      subtitle: "Environmental justice platform monitoring regulatory changes to protect frontline communities",
      cta: "Get Started",
      learnMore: "Learn More"
    },
    dashboard: {
      welcome: "Welcome back",
      totalPolicies: "Total Policies Tracked",
      stateSpecific: "State-Specific",
      highRisk: "High Risk",
      unreadAlerts: "Unread Alerts",
      recentPolicies: "Recent Policy Changes"
    },
    auth: {
      loginTitle: "Welcome Back",
      registerTitle: "Join PoliBot",
      email: "Email",
      password: "Password",
      orgName: "Organization Name",
      phone: "Phone (optional)",
      selectState: "Select State",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?"
    }
  },
  es: {
    nav: {
      dashboard: "Panel",
      policies: "Políticas",
      alerts: "Alertas",
      risk: "Evaluación de Riesgos",
      comments: "Generador de Comentarios",
      settings: "Configuración",
      logout: "Cerrar Sesión",
      login: "Iniciar Sesión",
      register: "Registrarse"
    },
    common: {
      loading: "Cargando...",
      save: "Guardar",
      cancel: "Cancelar",
      submit: "Enviar",
      search: "Buscar",
      filter: "Filtrar",
      all: "Todos",
      high: "Alto",
      medium: "Medio",
      low: "Bajo",
      federal: "Federal",
      state: "Estatal",
      local: "Local"
    },
    landing: {
      title: "Alertas Tempranas para Comunidades Más Seguras",
      subtitle: "Plataforma de justicia ambiental impulsada por IA que monitorea cambios regulatorios para proteger a las comunidades",
      cta: "Comenzar",
      learnMore: "Más Información"
    },
    dashboard: {
      welcome: "Bienvenido de nuevo",
      totalPolicies: "Políticas Totales Rastreadas",
      stateSpecific: "Específicas del Estado",
      highRisk: "Alto Riesgo",
      unreadAlerts: "Alertas Sin Leer",
      recentPolicies: "Cambios de Política Recientes"
    },
    auth: {
      loginTitle: "Bienvenido de Nuevo",
      registerTitle: "Únete a PoliBot",
      email: "Correo Electrónico",
      password: "Contraseña",
      orgName: "Nombre de la Organización",
      phone: "Teléfono (opcional)",
      selectState: "Seleccionar Estado",
      noAccount: "¿No tienes una cuenta?",
      haveAccount: "¿Ya tienes una cuenta?"
    }
  },
  fr: {
    nav: {
      dashboard: "Tableau de Bord",
      policies: "Politiques",
      alerts: "Alertes",
      risk: "Évaluation des Risques",
      comments: "Générateur de Commentaires",
      settings: "Paramètres",
      logout: "Déconnexion",
      login: "Connexion",
      register: "S'inscrire"
    },
    common: {
      loading: "Chargement...",
      save: "Sauvegarder",
      cancel: "Annuler",
      submit: "Soumettre",
      search: "Rechercher",
      filter: "Filtrer",
      all: "Tous",
      high: "Élevé",
      medium: "Moyen",
      low: "Faible",
      federal: "Fédéral",
      state: "État",
      local: "Local"
    },
    landing: {
      title: "Alertes Précoces pour des Communautés Plus Sûres",
      subtitle: "Plateforme de justice environnementale surveillant les changements réglementaires pour protéger les communautés",
      cta: "Commencer",
      learnMore: "En Savoir Plus"
    },
    dashboard: {
      welcome: "Bienvenue",
      totalPolicies: "Politiques Totales Suivies",
      stateSpecific: "Spécifiques à l'État",
      highRisk: "Risque Élevé",
      unreadAlerts: "Alertes Non Lues",
      recentPolicies: "Changements de Politique Récents"
    },
    auth: {
      loginTitle: "Bienvenue",
      registerTitle: "Rejoignez PoliBot",
      email: "E-mail",
      password: "Mot de Passe",
      orgName: "Nom de l'Organisation",
      phone: "Téléphone (facultatif)",
      selectState: "Sélectionner l'État",
      noAccount: "Pas de compte?",
      haveAccount: "Déjà un compte?"
    }
  },
  ar: {
    nav: {
      dashboard: "لوحة التحكم",
      policies: "السياسات",
      alerts: "التنبيهات",
      risk: "تقييم المخاطر",
      comments: "منشئ التعليقات",
      settings: "الإعدادات",
      logout: "تسجيل الخروج",
      login: "تسجيل الدخول",
      register: "التسجيل"
    },
    common: {
      loading: "جاري التحميل...",
      save: "حفظ",
      cancel: "إلغاء",
      submit: "إرسال",
      search: "بحث",
      filter: "تصفية",
      all: "الكل",
      high: "عالي",
      medium: "متوسط",
      low: "منخفض",
      federal: "فيدرالي",
      state: "ولاية",
      local: "محلي"
    },
    landing: {
      title: "تحذيرات مبكرة لمجتمعات أكثر أماناً",
      subtitle: "منصة العدالة البيئية لمراقبة التغييرات التنظيمية لحماية المجتمعات",
      cta: "ابدأ الآن",
      learnMore: "معرفة المزيد"
    },
    dashboard: {
      welcome: "مرحباً بعودتك",
      totalPolicies: "إجمالي السياسات المتتبعة",
      stateSpecific: "خاصة بالولاية",
      highRisk: "مخاطر عالية",
      unreadAlerts: "تنبيهات غير مقروءة",
      recentPolicies: "تغييرات السياسات الأخيرة"
    },
    auth: {
      loginTitle: "مرحباً بعودتك",
      registerTitle: "انضم إلى PoliBot",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      orgName: "اسم المنظمة",
      phone: "الهاتف (اختياري)",
      selectState: "اختر الولاية",
      noAccount: "ليس لديك حساب؟",
      haveAccount: "لديك حساب بالفعل؟"
    }
  },
  zh: {
    nav: {
      dashboard: "儀表板",
      policies: "政策",
      alerts: "警報",
      risk: "風險評估",
      comments: "評論生成器",
      settings: "設置",
      logout: "登出",
      login: "登入",
      register: "註冊"
    },
    common: {
      loading: "載入中...",
      save: "儲存",
      cancel: "取消",
      submit: "提交",
      search: "搜尋",
      filter: "篩選",
      all: "全部",
      high: "高",
      medium: "中",
      low: "低",
      federal: "聯邦",
      state: "州",
      local: "地方"
    },
    landing: {
      title: "為更安全社區提供早期預警",
      subtitle: "監測監管變化以保護前線社區的環境正義平台",
      cta: "立即開始",
      learnMore: "了解更多"
    },
    dashboard: {
      welcome: "歡迎回來",
      totalPolicies: "追蹤的政策總數",
      stateSpecific: "特定州",
      highRisk: "高風險",
      unreadAlerts: "未讀警報",
      recentPolicies: "最近的政策變化"
    },
    auth: {
      loginTitle: "歡迎回來",
      registerTitle: "加入 PoliBot",
      email: "電子郵件",
      password: "密碼",
      orgName: "組織名稱",
      phone: "電話（選填）",
      selectState: "選擇州",
      noAccount: "還沒有帳戶？",
      haveAccount: "已有帳戶？"
    }
  },
  ht: {
    nav: {
      dashboard: "Tablo Kontwòl",
      policies: "Règleman",
      alerts: "Alèt",
      risk: "Evalyasyon Risk",
      comments: "Jeneratè Kòmantè",
      settings: "Paramèt",
      logout: "Dekonekte",
      login: "Konekte",
      register: "Enskri",
      deedTheft: "Vòl Tit Pwopriyete",
      community: "Kominote",
      meetings: "Reyinyon"
    },
    common: {
      loading: "Chajman...",
      save: "Sove",
      cancel: "Anile",
      submit: "Soumèt",
      search: "Chèche",
      filter: "Filtre",
      all: "Tout",
      high: "Wo",
      medium: "Mwayen",
      low: "Ba",
      federal: "Federal",
      state: "Eta",
      local: "Lokal"
    },
    landing: {
      title: "Swiv Règleman ki Afekte Kominote Nou yo Plis",
      subtitle: "Platfòm jistis anviwònmantal pou kominote NYC. Swiv lojman, transpò, aksè manje, ti biznis, ak alèt vòl tit pwopriyete.",
      cta: "Kòmanse",
      learnMore: "Aprann Plis"
    },
    dashboard: {
      welcome: "Byenveni ankò",
      totalPolicies: "Total Règleman Swiv",
      stateSpecific: "Espesifik pou Eta",
      highRisk: "Risk Wo",
      unreadAlerts: "Alèt ki Pa Li",
      recentPolicies: "Dènye Chanjman Règleman"
    },
    auth: {
      loginTitle: "Byenveni Ankò",
      registerTitle: "Rejwenn PoliBot",
      email: "Imèl",
      password: "Modpas",
      orgName: "Non Òganizasyon",
      phone: "Telefòn (opsyonèl)",
      selectState: "Chwazi Eta",
      noAccount: "Ou pa gen yon kont?",
      haveAccount: "Ou gen deja yon kont?"
    },
    deedTheft: {
      title: "Alèt Vòl Tit Pwopriyete",
      subtitle: "Pwoteje pwopriyete ou epi rapòte aktivite sispèk",
      myReports: "Rapò Mwen",
      nycAlerts: "Alèt NYC",
      resources: "Resous",
      reportButton: "Rapòte Aktivite Sispèk",
      officialAcris: "Ofisyèl (ACRIS)",
      communityVerified: "Verifye pa Kominote"
    },
    meetings: {
      title: "Reyinyon Kominote",
      subtitle: "Rankontre ak vwazen ou yo epi patisipe",
      upcoming: "Reyinyon k ap Vini",
      rsvp: "RSVP",
      attending: "W ap Patisipe",
      date: "Dat",
      time: "Lè",
      location: "Kote",
      neighborhood: "Katye"
    }
  }
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-semibold">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    // Check for stored auth
    const token = localStorage.getItem('polibot_token');
    const storedUser = localStorage.getItem('polibot_user');
    const storedLang = localStorage.getItem('polibot_language');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('polibot_token');
        localStorage.removeItem('polibot_user');
      }
    }
    
    if (storedLang) {
      setLanguage(storedLang);
    }
    
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('polibot_token', token);
    localStorage.setItem('polibot_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('polibot_token');
    localStorage.removeItem('polibot_user');
    setUser(null);
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('polibot_language', lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      <LanguageContext.Provider value={{ language, changeLanguage, t }}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={
                <ProtectedRoute><Dashboard /></ProtectedRoute>
              } />
              <Route path="/policies" element={
                <ProtectedRoute><Policies /></ProtectedRoute>
              } />
              <Route path="/policies/:id" element={
                <ProtectedRoute><PolicyDetail /></ProtectedRoute>
              } />
              <Route path="/alerts" element={
                <ProtectedRoute><Alerts /></ProtectedRoute>
              } />
              <Route path="/risk" element={
                <ProtectedRoute><RiskAssessment /></ProtectedRoute>
              } />
              <Route path="/comments" element={
                <ProtectedRoute><CommentGenerator /></ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute><Settings /></ProtectedRoute>
              } />
              <Route path="/settings/privacy" element={
                <ProtectedRoute><Privacy /></ProtectedRoute>
              } />
              {/* Community Hub Routes */}
              <Route path="/community" element={
                <ProtectedRoute><CommunityHub /></ProtectedRoute>
              } />
              <Route path="/community/onboarding" element={
                <ProtectedRoute><CommunityOnboarding /></ProtectedRoute>
              } />
              <Route path="/community/discover" element={
                <ProtectedRoute><DiscoverAdvocates /></ProtectedRoute>
              } />
              <Route path="/community/profile/:id" element={
                <ProtectedRoute><CommunityProfile /></ProtectedRoute>
              } />
              <Route path="/community/connections" element={
                <ProtectedRoute><MyConnections /></ProtectedRoute>
              } />
              <Route path="/community/groups" element={
                <ProtectedRoute><Groups /></ProtectedRoute>
              } />
              <Route path="/community/messages" element={
                <ProtectedRoute><Messages /></ProtectedRoute>
              } />
              {/* Corporate Portal Routes */}
              <Route path="/corporate" element={
                <ProtectedRoute><CorporateDashboard /></ProtectedRoute>
              } />
              {/* Admin Portal Routes */}
              <Route path="/admin" element={
                <ProtectedRoute><AdminDashboard /></ProtectedRoute>
              } />
              {/* Deed Theft Routes */}
              <Route path="/deed-theft" element={
                <ProtectedRoute><DeedTheft /></ProtectedRoute>
              } />
              {/* Community Meetings Routes */}
              <Route path="/meetings" element={
                <ProtectedRoute><CommunityMeetings /></ProtectedRoute>
              } />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </LanguageContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
